/*
 * Hero collection — défilement horizontal auto ET contrôlable au doigt.
 * Contrairement au marquee CSS (animé par transform, non défilable), on pilote
 * le vrai scrollLeft d'un conteneur overflow-x:auto : boucle infinie sans
 * couture, et l'utilisateur peut attraper/scroller ; l'auto reprend ensuite.
 *
 * Le pan tactile/souris est piloté en JS (pointer events) pour maîtriser
 * l'inertie : on capte la vélocité du geste et on prolonge le glissement avec
 * une friction réglable, au lieu de dépendre du momentum natif du navigateur.
 */
class CustomHeroCollection extends HTMLElement {
  #abort = new AbortController();
  #rafId = 0;
  #lastTs = 0;
  #paused = false;
  #touching = false;
  #resumeTimer = 0;
  #loopWidth = 0;
  #dir = 1;
  #speed = 80;
  /* Délai avant reprise de l'auto après une interaction (ms). */
  #resumeDelay = 1200;
  /* Friction du momentum par frame (~60fps) : proche de 1 = glisse longtemps. */
  #friction = 0.957;
  /* Facteur d'échelle de la vélocité initiale du flick. */
  #inertiaScale = 1.1;
  /* Vitesse résiduelle du glissement (px/s) appliquée au relâchement. */
  #momentum = 0;
  #dragging = false;
  #pointerId = null;
  #startX = 0;
  #startScroll = 0;
  #lastX = 0;
  #lastT = 0;
  #velocity = 0;
  /** @type {HTMLElement | null} */ #wrapper = null;
  /** @type {HTMLElement | null} */ #content = null;
  /** @type {ResizeObserver | null} */ #ro = null;

  connectedCallback() {
    this.#wrapper = this.querySelector('.marquee__wrapper');
    this.#content = this.querySelector('.marquee__content');
    if (!this.#wrapper || !this.#content) return;

    this.#dir = this.dataset.movementDirection === 'reverse' ? -1 : 1;
    const factor = Number(this.dataset.speedFactor) || 25;
    this.#speed = Math.max(20, 2000 / factor);

    const rd = Number(this.dataset.resumeDelay);
    this.#resumeDelay = Number.isFinite(rd) ? Math.max(0, rd * 1000) : 1200;

    const inertia = Number(this.dataset.inertia);
    const inert = Number.isFinite(inertia) ? Math.min(100, Math.max(0, inertia)) : 60;
    // friction 0.90 (inertie 0, s'arrête vite) → 0.995 (inertie 100, glisse loin)
    this.#friction = 0.9 + (inert / 100) * 0.095;
    // ampleur du flick 0.5 → 1.5 selon l'inertie
    this.#inertiaScale = 0.5 + inert / 100;

    this.#buildClone();
    this.#measure();
    this.#bind();

    if (this.#dir === -1) this.scrollLeft = this.#loopWidth;

    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#play();
    }
  }

  disconnectedCallback() {
    this.#abort.abort();
    cancelAnimationFrame(this.#rafId);
    clearTimeout(this.#resumeTimer);
    this.#ro?.disconnect();
  }

  /** Duplique le contenu une fois pour la boucle sans couture. */
  #buildClone() {
    for (const old of this.#wrapper.querySelectorAll('[data-hero-clone]')) old.remove();
    const clone = this.#content.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('data-hero-clone', '');
    this.#wrapper.appendChild(clone);
  }

  #measure() {
    this.#loopWidth = this.#content.getBoundingClientRect().width;
  }

  #bind() {
    const { signal } = this.#abort;

    // Seuil (px) avant de considérer le geste comme un drag : préserve
    // les taps/clics sur les liens des panneaux.
    const DRAG_THRESHOLD = 6;

    this.addEventListener(
      'pointerdown',
      (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        this.#touching = true;
        this.#dragging = false;
        this.#momentum = 0;
        this.#paused = true;
        this.#pointerId = e.pointerId;
        this.#startX = e.clientX;
        this.#startScroll = this.scrollLeft;
        this.#lastX = e.clientX;
        this.#lastT = e.timeStamp;
        this.#velocity = 0;
        clearTimeout(this.#resumeTimer);
      },
      { signal }
    );

    this.addEventListener(
      'pointermove',
      (e) => {
        if (!this.#touching || e.pointerId !== this.#pointerId) return;
        const totalDx = e.clientX - this.#startX;
        if (!this.#dragging && Math.abs(totalDx) < DRAG_THRESHOLD) return;
        if (!this.#dragging) {
          this.#dragging = true;
          try {
            this.setPointerCapture(e.pointerId);
          } catch {
            /* capture best-effort */
          }
        }
        // On prend la main sur le pan horizontal (touch-action: pan-y côté CSS).
        e.preventDefault();
        this.scrollLeft = this.#startScroll - totalDx;
        this.#normalize();

        const dt = e.timeStamp - this.#lastT;
        if (dt > 0) {
          // Vélocité doigt lissée (px/ms) ; positif = vers la droite.
          const v = (e.clientX - this.#lastX) / dt;
          this.#velocity = this.#velocity * 0.7 + v * 0.3;
        }
        this.#lastX = e.clientX;
        this.#lastT = e.timeStamp;
      },
      { signal, passive: false }
    );

    const endDrag = (e) => {
      if (this.#pointerId !== null && e.pointerId !== this.#pointerId) return;
      this.#touching = false;
      this.#pointerId = null;
      if (this.#dragging) {
        // Doigt vers la droite (v>0) → contenu vers la droite → scrollLeft
        // diminue : le momentum scrollLeft est donc de signe opposé à v.
        this.#momentum = -this.#velocity * 1000 * this.#inertiaScale;
      }
      this.#scheduleResume(this.#resumeDelay);
      // Reset différé du flag pour bloquer le clic fantôme post-drag.
      setTimeout(() => {
        this.#dragging = false;
      }, 0);
    };
    this.addEventListener('pointerup', endDrag, { signal });
    this.addEventListener('pointercancel', endDrag, { signal });

    // Empêche la navigation quand on relâche après un vrai drag.
    this.addEventListener(
      'click',
      (e) => {
        if (this.#dragging) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      { signal, capture: true }
    );

    // Molette : petite pause de l'auto puis reprise.
    this.addEventListener(
      'wheel',
      () => {
        this.#paused = true;
        this.#momentum = 0;
        this.#scheduleResume(this.#resumeDelay);
      },
      { signal, passive: true }
    );

    let resizeTimer = 0;
    window.addEventListener(
      'resize',
      () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => this.#measure(), 200);
      },
      { signal }
    );

    if ('ResizeObserver' in window) {
      this.#ro = new ResizeObserver(() => this.#measure());
      this.#ro.observe(this.#content);
    }
  }

  #scheduleResume(delay) {
    clearTimeout(this.#resumeTimer);
    this.#resumeTimer = setTimeout(() => {
      this.#paused = false;
    }, delay);
  }

  #play() {
    const tick = (ts) => {
      if (!this.#lastTs) this.#lastTs = ts;
      let dt = (ts - this.#lastTs) / 1000;
      this.#lastTs = ts;
      // Onglet en arrière-plan : évite un saut sur reprise.
      if (dt > 0.1) dt = 0.1;

      if (!this.#touching) {
        if (Math.abs(this.#momentum) > 1) {
          // Glissement résiduel (inertie) avec décroissance de friction.
          this.scrollLeft += this.#momentum * dt;
          this.#momentum *= Math.pow(this.#friction, dt * 60);
          if (Math.abs(this.#momentum) < 24) this.#momentum = 0;
          this.#normalize();
        } else if (!this.#paused && this.#loopWidth > 0) {
          this.scrollLeft += this.#dir * this.#speed * dt;
          this.#normalize();
        } else {
          this.#normalize();
        }
      }

      this.#rafId = requestAnimationFrame(tick);
    };
    this.#rafId = requestAnimationFrame(tick);
  }

  /* Garde le scroll dans [0, loopWidth) : le 2e exemplaire assure la couture. */
  #normalize() {
    const w = this.#loopWidth;
    if (w <= 0) return;
    if (this.scrollLeft >= w) this.scrollLeft -= w;
    else if (this.scrollLeft < 0) this.scrollLeft += w;
  }
}

if (!customElements.get('custom-hero-collection')) {
  customElements.define('custom-hero-collection', CustomHeroCollection);
}
